import { useState, useEffect } from 'react';
import { Button, Input, Label, Tabs, TabsList, TabsTrigger, TabsContent } from '@fyos/components';
import { X, User, Lock, Info, AlertCircle, Check, Mail } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { profileApi, ProfileData } from '@/lib/profileApi';

function shortId(id: string): string {
  return id.slice(0, 8);
}

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ProfileModal({ open, onClose }: ProfileModalProps) {
  const { user, setUser } = useAuthStore();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile form — pre-populate from auth store immediately
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Sync from auth store when modal opens
  useEffect(() => {
    if (open && user) {
      setName(user.name);
      setEmail(user.email);
      loadProfile();
    }
  }, [open]);

  // Reset forms when modal closes
  useEffect(() => {
    if (!open) {
      setProfileSuccess('');
      setProfileError('');
      setPasswordSuccess('');
      setPasswordError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [open]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileApi.getProfile();
      setProfile(data);
      setName(data.name);
      setEmail(data.email);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    if (!name.trim()) {
      setProfileError('Name is required');
      return;
    }
    if (!email.trim()) {
      setProfileError('Email is required');
      return;
    }

    try {
      setProfileSaving(true);
      const updated = await profileApi.updateProfile({ name: name.trim(), email: email.trim() });
      setProfile(updated);
      setUser({
        id: updated.id,
        uuid: updated.uuid,
        name: updated.name,
        email: updated.email,
        avatar_url: updated.avatar_url,
      });
      setProfileSuccess('Profile updated successfully');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.errors?.name?.[0] ||
        'Failed to update profile';
      setProfileError(msg);
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      setPasswordSaving(true);
      await profileApi.changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      const msg =
        errors?.current_password?.[0] ||
        errors?.new_password?.[0] ||
        err.response?.data?.message ||
        'Failed to change password';
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const isOAuth = !!profile?.oauth_provider;
  const emailChanged = email.trim().toLowerCase() !== (profile?.email ?? user?.email ?? '').toLowerCase();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
              <User className="h-4.5 w-4.5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <Tabs defaultValue="profile">
            <TabsList className={`grid ${isOAuth ? 'grid-cols-2' : 'grid-cols-3'} w-full h-auto rounded-xl bg-gray-100 dark:bg-gray-800 p-1`}>
              <TabsTrigger value="profile" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <User className="h-4 w-4" />
                Profile
              </TabsTrigger>
              {!isOAuth && (
                <TabsTrigger value="password" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                  <Lock className="h-4 w-4" />
                  Password
                </TabsTrigger>
              )}
              <TabsTrigger value="account" className="flex-col gap-0.5 py-2 px-0 rounded-lg text-[10px] uppercase tracking-wide">
                <Info className="h-4 w-4" />
                Account
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="mt-6 space-y-5">
                {loading ? (
                  <div className="space-y-4">
                    <div className="animate-pulse h-10 rounded-md bg-gray-100 dark:bg-gray-700" />
                    <div className="animate-pulse h-10 rounded-md bg-gray-100 dark:bg-gray-700" />
                  </div>
                ) : (
                  <form onSubmit={handleProfileSave} className="space-y-4">
                    <div>
                      <Label htmlFor="profile-name" required>Full Name</Label>
                      <Input
                        id="profile-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        disabled={profileSaving}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="profile-email" required>Email Address</Label>
                      <Input
                        id="profile-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        disabled={profileSaving || isOAuth}
                        className="mt-1"
                      />
                      {isOAuth && (
                        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                          Email cannot be changed for {profile?.oauth_provider} accounts.
                        </p>
                      )}
                      {!isOAuth && emailChanged && (
                        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/30">
                          <Mail className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            Changing your email will also change the email you use to sign in. You'll need to use <strong>{email.trim()}</strong> for future logins.
                          </p>
                        </div>
                      )}
                    </div>

                    {profileError && (
                      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-950/30">
                        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600 dark:text-red-400">{profileError}</p>
                      </div>
                    )}

                    {profileSuccess && (
                      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-950/30">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <p className="text-sm text-green-600 dark:text-green-400">{profileSuccess}</p>
                      </div>
                    )}

                    <div className="flex justify-end pt-2">
                      <Button type="submit" disabled={profileSaving}>
                        {profileSaving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </TabsContent>

            {/* Password Tab */}
            {!isOAuth && (
              <TabsContent value="password">
                <div className="mt-6 space-y-5 d">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </p>

                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label htmlFor="current-password" required>Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        disabled={passwordSaving}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new-password" required>New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        disabled={passwordSaving}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="confirm-password" required>Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        disabled={passwordSaving}
                        className="mt-1"
                      />
                    </div>

                    {passwordError && (
                      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-950/30">
                        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-950/30">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <p className="text-sm text-green-600 dark:text-green-400">{passwordSuccess}</p>
                      </div>
                    )}

                    <div className="flex justify-end pt-2">
                      <Button type="submit" disabled={passwordSaving}>
                        {passwordSaving ? 'Changing...' : 'Change Password'}
                      </Button>
                    </div>
                  </form>
                </div>
              </TabsContent>
            )}

            {/* Account Tab */}
            <TabsContent value="account">
              <div className="mt-6 space-y-6 d">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Account Details</h3>
                  </div>
                  <dl className="divide-y divide-gray-200 dark:divide-gray-700">
                    <div className="flex justify-between px-4 py-3">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Name</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">{profile?.name ?? user?.name}</dd>
                    </div>
                    <div className="flex justify-between px-4 py-3">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Email</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">{profile?.email ?? user?.email}</dd>
                    </div>
                    <div className="flex justify-between items-center px-4 py-3">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">User ID</dt>
                      <dd
                        className="text-sm font-mono text-gray-500 dark:text-gray-400 cursor-default"
                        title={profile?.uuid ?? user?.uuid}
                      >
                        {(profile?.uuid ?? user?.uuid) ? shortId((profile?.uuid ?? user?.uuid)!) : '—'}
                      </dd>
                    </div>
                    {isOAuth && (
                      <div className="flex justify-between px-4 py-3">
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Auth Provider</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white capitalize">{profile?.oauth_provider}</dd>
                      </div>
                    )}
                    <div className="flex justify-between px-4 py-3">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Sign-in Method</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {isOAuth ? `${profile?.oauth_provider} (OAuth)` : 'Email & Password'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Danger Zone */}
                <div className="rounded-lg border border-red-200 dark:border-red-800/50 overflow-hidden">
                  <div className="px-4 py-3 bg-red-50 dark:bg-red-950/20">
                    <h3 className="text-sm font-medium text-red-700 dark:text-red-400">Danger Zone</h3>
                  </div>
                  <div className="px-4 py-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <Button variant="ghost" size="sm" disabled className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30">
                      Delete Account (Coming Soon)
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
